import React, { useEffect, useMemo, useState } from 'react';
import { createBanner, deleteBanner, getBanners, updateBanner } from '../../services/bannerService';
import { uploadFile } from '../../services/upload';
import './Banners.css';

const ENTITY_STATUS = {
  DELETED: 0,
  INACTIVE: 100,
  ACTIVE: 200,
};

const getStatusMeta = (value) => {
  const num = Number(value);
  if (num === ENTITY_STATUS.ACTIVE) return { label: 'Active', tone: 'success' };
  if (num === ENTITY_STATUS.INACTIVE) return { label: 'Inactive', tone: 'warning' };
  if (num === ENTITY_STATUS.DELETED) return { label: 'Deleted', tone: 'muted' };
  return { label: 'Unknown', tone: 'muted' };
};

const isLikelyImageUrl = (value) => {
  if (!value) return false;
  const s = String(value).trim();
  return /^https?:\/\//i.test(s) || /^data:image\//i.test(s);
};

const emptyLang = () => ({ uz: '', ru: '', oz: '' });

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(null);

  const [activeTab, setActiveTab] = useState('uz');
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    icon: emptyLang(),
    link: emptyLang(),
    status: ENTITY_STATUS.ACTIVE,
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  useEffect(() => {
    fetchBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const response = await getBanners({
        limit,
        offset,
        search,
      });
      setBanners(response?.data?.banners || []);
      setTotal(response?.data?.count || 0);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLangInputChange = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...(prev[field] || emptyLang()),
        [lang]: value,
      },
    }));
  };

  const handleScalarChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      handleLangInputChange('icon', activeTab, response?.data?.link || '');
    } catch (error) {
      console.error('Error uploading banner icon:', error);
    } finally {
      setUploading(false);
    }
  };

  const openModal = (banner = null) => {
    if (banner) {
      setCurrentBanner(banner);
      setFormData({
        name: banner?.name || '',
        icon: banner?.icon || emptyLang(),
        link: banner?.link || emptyLang(),
        status: Number(banner?.status ?? ENTITY_STATUS.ACTIVE),
      });
    } else {
      setCurrentBanner(null);
      setFormData({
        name: '',
        icon: emptyLang(),
        link: emptyLang(),
        status: ENTITY_STATUS.ACTIVE,
      });
    }
    setActiveTab('uz');
    setModalOpen(true);
  };

  const confirmDelete = (banner) => {
    setBannerToDelete(banner);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!bannerToDelete?.guid) return;
    try {
      await deleteBanner(bannerToDelete.guid);
      setDeleteModalOpen(false);
      setBannerToDelete(null);
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentBanner?.guid) {
        await updateBanner({
          guid: currentBanner.guid,
          name: formData.name,
          icon: formData.icon,
          link: formData.link,
          status: Number(formData.status),
        });
      } else {
        await createBanner({
          name: formData.name,
          icon: formData.icon,
          link: formData.link,
        });
      }
      setModalOpen(false);
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
    }
  };

  return (
    <div className="banners-page">
      <div className="page-header">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input type="text" placeholder="Search banners..." value={search} onChange={handleSearch} />
        </div>
        <div className="header-actions">
          <button className="add-btn" onClick={() => openModal()}>
            + Add Banner
          </button>
        </div>
      </div>

      <div className="banners-table-container" aria-busy={loading ? 'true' : 'false'}>
        <table className="banners-table">
          <thead>
            <tr>
              <th>№</th>
              <th>Icon</th>
              <th>Name</th>
              <th>Link (UZ)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && banners.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--text-secondary)' }}>
                  No banners found.
                </td>
              </tr>
            )}

            {banners.map((banner, index) => {
              const statusMeta = getStatusMeta(banner.status);
              const iconUz = banner?.icon?.uz || '';
              const linkUz = banner?.link?.uz || '';

              return (
                <tr key={banner.guid}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>
                    {isLikelyImageUrl(iconUz) ? (
                      <img src={iconUz} alt={banner?.name || 'Banner icon'} className="banner-icon" />
                    ) : (
                      <div className="banner-icon-fallback" title={iconUz || 'No icon'}>
                        IMG
                      </div>
                    )}
                  </td>
                  <td>{banner?.name}</td>
                  <td style={{ maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={linkUz}>
                    {linkUz}
                  </td>
                  <td>
                    <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
                  </td>
                  <td>
                    <button className="action-btn" onClick={() => openModal(banner)} aria-label="Edit banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button className="action-btn delete" onClick={() => confirmDelete(banner)} aria-label="Delete banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}

            {loading && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--text-secondary)' }}>
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          {total > 0 ? (
            <>
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
            </>
          ) : (
            <>Showing 0 entries</>
          )}
        </div>
        <div className="pagination-controls">
          <button disabled={page === 1} onClick={() => handlePageChange(page - 1)} className="page-btn">
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button key={i + 1} className={`page-btn ${page === i + 1 ? 'active' : ''}`} onClick={() => handlePageChange(i + 1)}>
              {i + 1}
            </button>
          ))}
          <button disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)} className="page-btn">
            Next
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Delete banner">
          <div className="modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Delete Banner</h2>
              <button className="close-btn" onClick={() => setDeleteModalOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to delete the banner <strong>{bannerToDelete?.name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-save" style={{ backgroundColor: 'var(--error)' }} onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Banner form">
          <div className="modal">
            <div className="modal-header">
              <h2>{currentBanner ? 'Edit Banner' : 'Add Banner'}</h2>
              <button className="close-btn" onClick={() => setModalOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => handleScalarChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={String(formData.status)}
                      onChange={(e) => handleScalarChange('status', Number(e.target.value))}
                      disabled={!currentBanner}
                      aria-disabled={!currentBanner ? 'true' : 'false'}
                    >
                      <option value={ENTITY_STATUS.ACTIVE}>Active</option>
                      <option value={ENTITY_STATUS.INACTIVE}>Inactive</option>
                      <option value={ENTITY_STATUS.DELETED}>Deleted</option>
                    </select>
                    {!currentBanner && (
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
                        Status is set on update by the backend.
                      </div>
                    )}
                  </div>
                </div>

                <div className="lang-tabs" aria-label="Language tabs">
                  {['uz', 'ru', 'oz'].map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      className={`lang-tab ${activeTab === lang ? 'active' : ''}`}
                      onClick={() => setActiveTab(lang)}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Icon ({activeTab.toUpperCase()})</label>
                    <div className="file-upload" onClick={() => document.getElementById('banner-icon-input').click()}>
                      {isLikelyImageUrl(formData.icon?.[activeTab]) ? (
                        <img src={formData.icon[activeTab]} alt="Preview" className="preview-image" />
                      ) : (
                        <p style={{ color: 'var(--text-secondary)' }}>Click to upload icon image (optional)</p>
                      )}
                      <input
                        id="banner-icon-input"
                        type="file"
                        accept="image/*"
                        onChange={handleIconFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {uploading && <p style={{ color: 'var(--text-secondary)' }}>Uploading...</p>}
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Or paste an icon URL"
                      value={formData.icon?.[activeTab] || ''}
                      onChange={(e) => handleLangInputChange('icon', activeTab, e.target.value)}
                      style={{ marginTop: 10 }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Link ({activeTab.toUpperCase()})</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.link?.[activeTab] || ''}
                      onChange={(e) => handleLangInputChange('link', activeTab, e.target.value)}
                      placeholder="https://..."
                    />
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
                      Store per-language deep links if needed.
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={uploading}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;
